import { useState, useEffect, useCallback, useRef } from 'react';

import { logAdminAction } from '@lift-admin/utils/actionLogTrack';
import {
  buildDetails,
  diffFields,
  diffList,
  type FieldSpec,
  type LogChange,
  type LogDetail,
} from '@lift-admin/utils/logChangeFormatter';

import { Alert, PaginationData, AlertFilters, AlertConfigResponse } from '../types';
import { parseCronExpression } from '../utils/cronParser';
import { dmgAlertApiService } from '../utils/dmgAlertApiService';
import { mapApiResponseToAlert } from '../utils/mappers';

const DMG_ALERT_API_BASE_URL = process.env.DMG_ALERT_API_URL;

// Scalar fields shared by the API response and update payload. Nested objects
// (message_template, schedule_config, recipients) are handled by custom diffs below.
// Note: payload cadence is always forced to 'realtime' — real schedule lives in
// schedule_config / cron_info, so cadence is intentionally omitted here.
const ALERT_FIELD_SPECS: FieldSpec[] = [
  { key: 'name', label: 'Name' },
  { key: 'description', label: 'Description' },
  { key: 'alert_type_id', label: 'Alert Type ID' },
  { key: 'enable_slack', label: 'Slack Notifications', type: 'boolean' },
  { key: 'enable_email', label: 'Email Notifications', type: 'boolean' },
  { key: 'enable_lift_notification', label: 'Lift Notifications', type: 'boolean' },
];

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const LOG_TEXT_CLIP = 200;

const clipLogText = (value?: string | null, max = LOG_TEXT_CLIP): string => {
  const text = String(value ?? '').trim();
  if (!text) return 'Not Set';
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

// Normalize template / HTML / Slack markdown to comparable plain text for diffs.
const normalizeTemplateText = (value: any): string => {
  if (value == null) return '';
  let text = String(value);
  // Strip HTML tags from email/web templates
  text = text.replace(/<[^>]*>/g, ' ');
  // Decode a few common entities
  text = text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"');
  // Collapse Slack mrkdwn markers for equality checks
  text = text.replace(/[*_~`]+/g, '');
  return text.replace(/\s+/g, ' ').trim();
};

const TEMPLATE_FIELDS: Array<{ key: 'msg' | 'slack' | 'email' | 'web'; label: string }> = [
  { key: 'msg', label: 'Alert Prompt' },
  { key: 'slack', label: 'Slack Template' },
  { key: 'email', label: 'Email Template' },
  { key: 'web', label: 'Web Template' },
];

const formatDayList = (days: Array<string | number> | undefined): string => {
  if (!Array.isArray(days) || days.length === 0) return 'Not Set';
  return days
    .map(d => {
      const n = Number(d);
      return DAY_NAMES[n - 1] ?? String(d);
    })
    .join(', ');
};

const describeSchedule = (source: any): string => {
  const cron = source?.cron_info != null ? String(source.cron_info).trim() : '';
  const parsed = cron ? parseCronExpression(cron) : null;
  const sched = source?.schedule_config ?? {};
  const cadence = parsed?.cadence ?? 'custom';
  const time = parsed?.time ?? sched.time ?? 'Not Set';
  const timezone = sched.timezone ?? 'Not Set';

  const parts = [`${cadence}`, `at ${time}`, timezone !== 'Not Set' ? `(${timezone})` : ''];

  if (cadence === 'weekly') {
    const days = parsed?.days ?? sched.days;
    if (days?.length) parts.push(`days: ${formatDayList(days)}`);
  }
  if (cadence === 'monthly') {
    // Prefer cron-derived day — schedule_config.day is hardcoded to 1 in the mapper.
    const day = Array.isArray(parsed?.day)
      ? parsed!.day.join(',')
      : (parsed?.day ?? sched.day);
    if (day != null && day !== '') parts.push(`day of month: ${day}`);
  }
  if (cron) parts.push(`[cron: ${cron}]`);

  return parts.filter(Boolean).join(' ');
};

const describeRecipient = (r: any): string => {
  if (!r || typeof r !== 'object') return String(r ?? '');
  const type = String(r.recipient_type ?? '').toLowerCase();
  const value = r.recipient_value;

  if (type.includes('slack')) {
    const name = typeof value === 'string' ? value : String(r.recipient_id ?? '');
    const kind = type.includes('channel') ? 'channel' : 'user';
    return name ? `${kind}: ${name}` : String(r.recipient_id ?? '');
  }

  if (type.includes('email')) {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') {
      const email = value.email ?? value.name ?? '';
      return email ? String(email) : String(r.recipient_id ?? '');
    }
    return String(r.recipient_id ?? '');
  }

  if (type === 'web_user' || (value && typeof value === 'object' && value.user_id != null)) {
    const name =
      (value && typeof value === 'object' && (value.name || value.email)) ||
      r.recipient_id ||
      value?.user_id;
    return `user: ${name}`;
  }

  if (type === 'web_brand' || r.brand_id != null || (value && typeof value === 'object' && value.brand_id != null)) {
    const brandName = r.brand_name ?? (value && typeof value === 'object' ? value.name : null);
    const brandId = r.brand_id ?? value?.brand_id ?? r.recipient_id;
    return brandName ? `brand: ${brandName} (${brandId})` : `brand: ${brandId}`;
  }

  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    return String(value.email ?? value.name ?? value.brand_id ?? r.recipient_id ?? JSON.stringify(value));
  }
  return String(r.recipient_id ?? r.brand_id ?? '');
};

const extractRecipientLabels = (source: any, channel: 'slack' | 'email' | 'email_cc' | 'web'): string[] => {
  const list = source?.recipients?.[channel];
  if (!Array.isArray(list)) return [];
  return list.map(describeRecipient).filter(Boolean).sort();
};

const buildTemplateDetails = (source: any): LogDetail[] => {
  const template = source?.message_template ?? {};
  const details: LogDetail[] = [];
  for (const { key, label } of TEMPLATE_FIELDS) {
    const raw = template[key];
    const normalized = normalizeTemplateText(raw);
    if (!normalized) continue;
    details.push({ label, value: clipLogText(normalized) });
  }
  return details;
};

const buildScheduleDetails = (source: any): LogDetail[] => {
  const summary = describeSchedule(source);
  if (!summary || summary === 'custom at Not Set') return [];
  return [{ label: 'Schedule', value: summary }];
};

const buildRecipientDetails = (source: any): LogDetail[] => {
  const details: LogDetail[] = [];
  const channels: Array<{ key: 'slack' | 'email' | 'email_cc' | 'web'; label: string }> = [
    { key: 'slack', label: 'Slack Recipients' },
    { key: 'email', label: 'Email Recipients' },
    { key: 'email_cc', label: 'Email CC Recipients' },
    { key: 'web', label: 'Lift Recipients' },
  ];
  for (const { key, label } of channels) {
    const labels = extractRecipientLabels(source, key);
    if (labels.length) {
      details.push({ label, value: labels.join(', ') });
    }
  }
  return details;
};

const diffMessageTemplates = (previous: any, alertData: any): LogChange[] => {
  const changes: LogChange[] = [];
  const oldT = previous?.message_template ?? {};
  const newT = alertData?.message_template ?? {};

  // Only diff when the update payload includes message_template (always does on save).
  if (alertData?.message_template == null) return changes;

  for (const { key, label } of TEMPLATE_FIELDS) {
    const fromNorm = normalizeTemplateText(oldT[key]);
    const toNorm = normalizeTemplateText(newT[key]);
    if (fromNorm === toNorm) continue;
    changes.push({
      label,
      from: clipLogText(fromNorm || 'Not Set'),
      to: clipLogText(toNorm || 'Not Set'),
    });
  }
  return changes;
};

const diffScheduleConfig = (previous: any, alertData: any): LogChange[] => {
  const changes: LogChange[] = [];
  if (alertData?.schedule_config == null && alertData?.cron_info == null) return changes;

  const oldSched = previous?.schedule_config ?? {};
  const newSched = alertData?.schedule_config ?? {};

  const oldCron = previous?.cron_info != null ? String(previous.cron_info).trim() : '';
  const newCron = alertData?.cron_info != null ? String(alertData.cron_info).trim() : '';

  const oldParsed = oldCron ? parseCronExpression(oldCron) : null;
  const newParsed = newCron ? parseCronExpression(newCron) : null;

  // Timezone lives only on schedule_config (not in cron) — always compare.
  const oldTz = String(oldSched.timezone ?? '').trim();
  const newTz = String(newSched.timezone ?? '').trim();
  if (oldTz !== newTz && (oldTz || newTz)) {
    changes.push({
      label: 'Timezone',
      from: oldTz || 'Not Set',
      to: newTz || 'Not Set',
    });
  }

  const cronChanged = oldCron !== newCron && (oldCron || newCron);

  // Humanize schedule parts when the cron expression changed (or when we only
  // have one side). Prefer cron-derived values so mapper quirks (e.g. monthly
  // day hardcoded to 1 on schedule_config) do not create false diffs.
  if (cronChanged || (!oldCron && newCron) || (oldCron && !newCron)) {
    const oldCadence = oldParsed?.cadence ?? '';
    const newCadence = newParsed?.cadence ?? '';
    if (oldCadence !== newCadence && (oldCadence || newCadence)) {
      changes.push({
        label: 'Schedule Cadence',
        from: oldCadence || 'Not Set',
        to: newCadence || 'Not Set',
      });
    }

    const oldTime = String(oldParsed?.time ?? oldSched.time ?? '');
    const newTime = String(newParsed?.time ?? newSched.time ?? '');
    if (oldTime !== newTime && (oldTime || newTime)) {
      changes.push({
        label: 'Schedule Time',
        from: oldTime || 'Not Set',
        to: newTime || 'Not Set',
      });
    }

    const oldDays = (oldParsed?.days ?? []).map(String).sort();
    const newDays = (newParsed?.days ?? []).map(String).sort();
    if (oldDays.join(',') !== newDays.join(',')) {
      const dayDiff = diffList(oldDays, newDays);
      if (dayDiff.added.length || dayDiff.removed.length) {
        changes.push({
          label: 'Schedule Days',
          added: dayDiff.added.map(d => DAY_NAMES[Number(d) - 1] ?? d),
          removed: dayDiff.removed.map(d => DAY_NAMES[Number(d) - 1] ?? d),
        });
      }
    }

    const oldDayOfMonth = Array.isArray(oldParsed?.day)
      ? oldParsed!.day.join(',')
      : String(oldParsed?.day ?? '');
    const newDayOfMonth = Array.isArray(newParsed?.day)
      ? newParsed!.day.join(',')
      : String(newParsed?.day ?? '');
    if (oldDayOfMonth !== newDayOfMonth && (oldDayOfMonth || newDayOfMonth)) {
      changes.push({
        label: 'Schedule Day of Month',
        from: oldDayOfMonth || 'Not Set',
        to: newDayOfMonth || 'Not Set',
      });
    }

    if (cronChanged) {
      changes.push({
        label: 'Schedule (cron)',
        from: oldCron || 'Not Set',
        to: newCron || 'Not Set',
      });
    }
  }

  return changes;
};

const diffRecipients = (previous: any, alertData: any): LogChange[] => {
  const changes: LogChange[] = [];
  if (alertData?.recipients == null) return changes;

  const channels: Array<{ key: 'slack' | 'email' | 'email_cc' | 'web'; label: string }> = [
    { key: 'slack', label: 'Slack Recipients' },
    { key: 'email', label: 'Email Recipients' },
    { key: 'email_cc', label: 'Email CC Recipients' },
    { key: 'web', label: 'Lift Recipients' },
  ];

  for (const { key, label } of channels) {
    const oldLabels = extractRecipientLabels(previous, key);
    const newLabels = extractRecipientLabels(alertData, key);
    const { added, removed } = diffList(oldLabels, newLabels);
    if (added.length || removed.length) {
      changes.push({ label, added, removed });
    }
  }
  return changes;
};

// Build a readable detail list from an alert API payload (create/update).
const buildAlertDetails = (alertData: any) => {
  const details = buildDetails(alertData ?? {}, ALERT_FIELD_SPECS, { skipEmpty: true });

  const brandIds: number[] = Array.isArray(alertData?.brand_ids) ? alertData.brand_ids : [];
  if (brandIds.length) {
    details.push({ label: 'Brands', value: `${brandIds.length} (IDs: ${brandIds.join(', ')})` });
  }

  // List the actual conditions so the log shows exactly what was configured.
  details.push(...buildConditionSnapshot(alertData));
  details.push(...buildScheduleDetails(alertData));
  details.push(...buildTemplateDetails(alertData));
  details.push(...buildRecipientDetails(alertData));

  return details;
};

// Extract a metric name from the many shapes it can take:
//  - string:            "spend"
//  - object:            { name: "spend" }
//  - array of objects:  [{ name: "spend" }]  (current pacing payload shape)
const extractMetricName = (metric: any): string => {
  if (metric == null) return '';
  if (Array.isArray(metric)) {
    return metric.map(extractMetricName).filter(Boolean).join('+');
  }
  if (typeof metric === 'object') {
    return String(metric.name ?? metric.metric ?? metric.label ?? '').trim();
  }
  return String(metric).trim();
};

// Pacing alerts store operators as budget-variance symbols (+/-). Map them to the
// comparison operators shown in the UI so logs read the same as the alert form.
const PACING_OPERATOR_MAP: Record<string, string> = {
  '+': '>',
  '-': '<',
  '+-': '><',
  '+=': '>=',
  '-=': '<=',
  '+-=': '>=<=',
};

const mapOperator = (raw: any): string => {
  const op = String(raw ?? '').trim();
  return PACING_OPERATOR_MAP[op] ?? op;
};

// Normalize a condition's dimension into a stable, readable label that is IDENTICAL
// whether it comes from the update payload or the stored API response. Both carry
// dimension as an array of { name (child), type (parent) } in the new pacing format,
// or a `dimension_name` string in the old format.
const describeDimension = (c: any): string => {
  const pairFromObj = (d: any): string => {
    if (!d || typeof d !== 'object') return String(d ?? '').trim();
    const child = String(d.name ?? d.child ?? '').trim();
    const parent = String(d.type ?? d.parent ?? '').trim();
    if (parent && parent.toUpperCase() === 'ALL_DIMENSION') return 'All';
    if (parent && child) return `${parent}: ${child}`;
    return child || parent;
  };

  if (Array.isArray(c?.dimension)) {
    return c.dimension.map(pairFromObj).filter(Boolean).sort().join(', ');
  }
  if (typeof c?.dimension === 'string') return c.dimension.trim();
  if (typeof c?.dimension_name === 'string') return c.dimension_name.trim();
  return '';
};

// Whether the threshold value is a percentage (so we render a "%" unit). Only
// pacing and taxonomy conditions carry a value type; EMQ/other metrics have none
// and must NOT get a "%" suffix. Consistent across both shapes (API response uses
// snake_case, UI-mapped state uses camelCase).
const isPercentageValue = (c: any): boolean => {
  const vt = c?.pacing_value_type ?? c?.pacingValueType ??
    c?.taxonomy_value_type ?? c?.taxonomyValueType;
  return vt === 'percentage';
};

// Stable identity for a condition — everything that identifies WHICH condition it is
// (metric + dimension + pixel/event) but NOT the operator/value that can be edited.
// This lets us detect an edited threshold as a change instead of an unrelated
// add/remove pair, and keeps dimension-distinct rows (same metric, different channel)
// from collapsing into one. comparison_period is deliberately excluded because the
// API response and the update payload encode it differently (period vs date range).
const conditionIdentity = (c: any): string => {
  const metric =
    extractMetricName(c?.metric ?? c?.metrics_name ?? c?.metric_name) || 'metric';
  const parts = [`metric:${metric.toLowerCase()}`];
  const dim = describeDimension(c);
  if (dim) parts.push(`dim:${dim.toLowerCase()}`);
  if (c?.pixel_name) parts.push(`pixel:${String(c.pixel_name).toLowerCase()}`);
  if (c?.event_name) parts.push(`event:${String(c.event_name).toLowerCase()}`);
  return parts.join(' | ');
};

// Full, human-readable description of a single condition, e.g.
// "spend > 50% (Channel: Search)". Built identically for the stored alert and the
// update payload so equal conditions produce equal strings (no false diffs).
const describeCondition = (c: any): string => {
  if (!c || typeof c !== 'object') return '';
  const metric =
    extractMetricName(c.metric ?? c.metrics_name ?? c.metric_name) || 'metric';
  const operator = mapOperator(c.operator);
  const rawValue = c.value ?? c.threshold_value ?? c.threshold;
  const valueStr = rawValue === undefined || rawValue === null ? '' : String(rawValue);
  const valueWithUnit =
    valueStr && isPercentageValue(c) ? `${valueStr}%` : valueStr;

  let label = `${metric} ${operator} ${valueWithUnit}`.replace(/\s+/g, ' ').trim();

  const context: string[] = [];
  const dim = describeDimension(c);
  if (dim) context.push(dim);
  if (c.pixel_name) context.push(`pixel: ${c.pixel_name}`);
  if (c.event_name) context.push(`event: ${c.event_name}`);
  if (context.length) label += ` (${context.join(', ')})`;

  return label;
};

interface DescribedCondition {
  identity: string;
  text: string;
}

// Extract structured, described conditions from either an update payload
// (threshold_configs: group[][]) or an API response (brands[].threshold_config: group[][]).
const describeAlertConditions = (source: any): DescribedCondition[] => {
  const out: DescribedCondition[] = [];

  // Flatten an arbitrarily nested array of condition objects and describe each.
  const collect = (node: any) => {
    if (Array.isArray(node)) {
      node.forEach(collect);
      return;
    }
    if (!node || typeof node !== 'object') return;
    const text = describeCondition(node);
    if (text) out.push({ identity: conditionIdentity(node), text });
  };

  if (Array.isArray(source?.threshold_configs)) {
    collect(source.threshold_configs);
  }
  if (Array.isArray(source?.brands)) {
    source.brands.forEach((brand: any) => {
      if (brand?.threshold_config != null) {
        collect(brand.threshold_config);
      }
    });
  }
  return out;
};

// A complete, always-accurate snapshot of the resulting threshold conditions,
// derived solely from the submitted payload (independent of any previous state).
const buildConditionSnapshot = (source: any): LogDetail[] => {
  const conditions = describeAlertConditions(source);
  return conditions.map((c, i) => ({
    label: `Condition ${i + 1}`,
    value: c.text,
  }));
};

// Derive brand IDs from either an update payload (brand_ids) or an API response (brands[]).
const extractBrandIds = (source: any): Array<string | number> => {
  if (Array.isArray(source?.brand_ids)) return source.brand_ids;
  if (Array.isArray(source?.brands)) {
    return source.brands
      .map((b: any) => b?.brand_id)
      .filter((id: any) => id != null);
  }
  return [];
};

// Build a readable list of before -> after changes between the previous alert
// (API response shape) and the new update payload.
const buildAlertChanges = (previous: any, alertData: any): LogChange[] => {
  const changes: LogChange[] = [];
  if (!previous) return changes;

  // Only diff scalar fields present in BOTH the previous alert and the update payload,
  // to avoid false "Not Set -> X" entries for fields one side simply does not carry.
  const scalarSpecs = ALERT_FIELD_SPECS.filter(
    spec => alertData?.[spec.key] !== undefined && previous?.[spec.key] !== undefined
  );
  changes.push(...diffFields(previous, alertData, scalarSpecs));

  if (Array.isArray(alertData?.brand_ids)) {
    const prevBrandIds = extractBrandIds(previous).map(String);
    const newBrandIds = alertData.brand_ids.map(String);
    const brandDiff = diffList(prevBrandIds, newBrandIds);
    if (brandDiff.added.length || brandDiff.removed.length) {
      changes.push({ label: 'Brands', added: brandDiff.added, removed: brandDiff.removed });
    }
  }

  if (Array.isArray(alertData?.threshold_configs)) {
    const oldConditions = describeAlertConditions(previous);
    const newConditions = describeAlertConditions(alertData);

    // Bucket conditions by their stable identity (metric + dimension + pixel/event)
    // so an edited threshold value is reported as a single change rather than an
    // unrelated add + remove, and dimension-distinct rows never collapse together.
    const bucketByIdentity = (list: DescribedCondition[]) => {
      const map = new Map<string, string[]>();
      list.forEach(c => {
        const texts = map.get(c.identity) ?? [];
        texts.push(c.text);
        map.set(c.identity, texts);
      });
      return map;
    };

    const oldByIdentity = bucketByIdentity(oldConditions);
    const newByIdentity = bucketByIdentity(newConditions);

    const added: string[] = [];
    const removed: string[] = [];

    const identities = new Set<string>([
      ...oldByIdentity.keys(),
      ...newByIdentity.keys(),
    ]);

    identities.forEach(identity => {
      const olds = oldByIdentity.get(identity) ?? [];
      const news = newByIdentity.get(identity) ?? [];

      // A single condition on each side sharing the same identity = an in-place edit
      // (e.g. only the value/operator changed). Show it as a before -> after pair.
      if (olds.length === 1 && news.length === 1) {
        if (olds[0] !== news[0]) {
          removed.push(olds[0]);
          added.push(news[0]);
        }
        return;
      }

      // Otherwise fall back to a set difference within this identity bucket.
      news.forEach(text => {
        if (!olds.includes(text)) added.push(text);
      });
      olds.forEach(text => {
        if (!news.includes(text)) removed.push(text);
      });
    });

    if (added.length || removed.length) {
      changes.push({ label: 'Threshold Conditions', added, removed });
    }
  }

  // Nested config that scalar FieldSpecs cannot express meaningfully.
  changes.push(...diffMessageTemplates(previous, alertData));
  changes.push(...diffScheduleConfig(previous, alertData));
  changes.push(...diffRecipients(previous, alertData));

  return changes;
};

interface UseAlertsReturn {
  alerts: Alert[];
  loading: boolean;
  pagination: PaginationData;
  error: string | null;
  fetchAlerts: (
    page?: number,
    pageSize?: number,
    filters?: AlertFilters,
    alertType?: 'my-alerts' | 'global-alerts'
  ) => Promise<void>;
  createAlert: (alertData: any) => Promise<Alert | null>;
  updateAlert: (alertId: number, userId: number, alertData: any) => Promise<Alert | null>;
  deleteAlert: (alertId: number, userId: number) => Promise<boolean>;
  activateAlert: (alertId: number, userId: number) => Promise<boolean>;
  deactivateAlert: (alertId: number, userId: number) => Promise<boolean>;
  sendTestAlert: (id: string) => Promise<boolean>;
  subscribeToGlobalAlert: (id: string) => Promise<boolean>;
  unsubscribeFromGlobalAlert: (id: string) => Promise<boolean>;
  subscribeToMyAlert: (userId: number, alertId: number) => Promise<boolean>;
  unsubscribeFromMyAlert: (userId: number, alertId: number) => Promise<boolean>;
  refreshAlerts: () => Promise<void>;
}

export const useAlerts = (
  initialPage: number = 1,
  initialPageSize: number = 15,
  alertType: 'my-alerts' | 'global-alerts' = 'my-alerts'
): UseAlertsReturn => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    currentPage: initialPage,
    pageSize: initialPageSize,
    totalItems: 0,
    totalPages: 0,
  });
  const [error, setError] = useState<string | null>(null);

  // Keep latest alerts in a ref so memoized callbacks can resolve names for logging
  // without changing their identity.
  const alertsRef = useRef<Alert[]>([]);
  useEffect(() => {
    alertsRef.current = alerts;
  }, [alerts]);

  // Cache the raw API responses (keyed by id) so update logs can build an accurate
  // before -> after diff without relying on a separate (and possibly unsupported)
  // GET-by-id request.
  const rawAlertsRef = useRef<Record<number, AlertConfigResponse>>({});

  const getAlertName = useCallback((alertId: number): string => {
    const found = alertsRef.current.find(a => a.id === String(alertId));
    return found?.name ? `"${found.name}"` : `#${alertId}`;
  }, []);

  const fetchAlerts = useCallback(
    async (
      page: number = pagination.currentPage,
      pageSize: number = pagination.pageSize,
      filters?: AlertFilters,
      currentAlertType: 'my-alerts' | 'global-alerts' = alertType
    ) => {
      setLoading(true);
      setError(null);

      try {
        const result = await dmgAlertApiService.getAlerts(
          page,
          pageSize,
          filters,
          currentAlertType
        );

        // Filter out inactive alerts by default.
        // If filters.is_active is explicitly false, return only inactive alerts.
        let apiAlerts = result.data;
        if (filters?.is_active === false) {
          apiAlerts = apiAlerts.filter(alert => alert.is_active === false);
        } else {
          apiAlerts = apiAlerts.filter(alert => alert.is_active !== false);
        }

        // Cache raw responses (before filtering) so update logs can diff against them.
        result.data.forEach(raw => {
          if (raw && raw.id != null) {
            rawAlertsRef.current[raw.id] = raw;
          }
        });

        // Map API responses to frontend Alert format
        const mappedAlerts = apiAlerts.map(mapApiResponseToAlert);
        setAlerts(mappedAlerts);
        setPagination(result.pagination);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch alerts');
        console.error('Error fetching alerts:', err);
      } finally {
        setLoading(false);
      }
    },
    [alertType]
  );

  const createAlert = useCallback(async (alertData: any): Promise<Alert | null> => {
    setLoading(true);
    setError(null);

    try {
      const created = await dmgAlertApiService.createAlert(alertData);
      // Don't refresh here - let the parent component handle refresh with correct filters and tab context
      await logAdminAction(
        `Alert "${alertData?.name ?? 'Untitled'}" created`,
        {
          url: `${DMG_ALERT_API_BASE_URL}/api/v1/alerts`,
          method: 'POST',
          status: 200,
          category: 'alert',
          action: 'create_alert',
          alertId: (created as any)?.id,
          details: buildAlertDetails(alertData),
        }
      );
      return null;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create alert');
      console.error('Error creating alert:', err);
      await logAdminAction(
        `Failed to create alert "${alertData?.name ?? 'Untitled'}": ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        { url: `${DMG_ALERT_API_BASE_URL}/api/v1/alerts`, method: 'POST', category: 'alert', action: 'create_alert' },
        true
      );
      throw err; // Re-throw to let parent handle it
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAlert = useCallback(
    async (alertId: number, userId: number, alertData: any): Promise<Alert | null> => {
      setLoading(true);
      setError(null);

      // Snapshot the current alert BEFORE updating so we can show before -> after
      // changes in the log. Prefer the cached raw API response (already loaded for the
      // list/edit form); fall back to a best-effort GET. Never block/break the update.
      let previousAlert: any = rawAlertsRef.current[alertId] ?? null;
      if (!previousAlert) {
        try {
          previousAlert = await dmgAlertApiService.getAlertById(alertId);
        } catch (snapshotErr) {
          console.warn('Could not fetch previous alert for change log:', snapshotErr);
        }
      }

      try {
        await dmgAlertApiService.updateAlert(alertId, userId, alertData);
        // Don't refresh here - let the parent component handle refresh with correct filters and tab context
        const changes = buildAlertChanges(previousAlert, alertData);
        const changeSummary = changes
          .map(c => {
            if (c.added || c.removed) {
              const parts = [
                ...(c.added ?? []).map(a => `+${a}`),
                ...(c.removed ?? []).map(r => `-${r}`),
              ];
              return parts.length ? `${c.label}: ${parts.join(', ')}` : c.label;
            }
            return `${c.label}: ${c.from} → ${c.to}`;
          })
          .join('; ');
        await logAdminAction(
          `Alert "${alertData?.name ?? `#${alertId}`}" updated${
            changes.length
              ? ` — ${changeSummary}`
              : previousAlert
                ? ' (no field changes detected)'
                : ''
          }`,
          {
            url: `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/${alertId}`,
            method: 'PUT',
            status: 200,
            category: 'alert',
            action: 'update_alert',
            alertId,
            userId,
            changes,
            // Always include the resulting config snapshot so the log shows the
            // exact state after the update, even if a nested diff was missed.
            details: [
              { label: 'Alert ID', value: String(alertId) },
              ...buildConditionSnapshot(alertData),
              ...buildScheduleDetails(alertData),
              ...buildTemplateDetails(alertData),
              ...buildRecipientDetails(alertData),
            ],
          }
        );
        return null;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update alert');
        console.error('Error updating alert:', err);
        await logAdminAction(
          `Failed to update alert #${alertId}: ${err instanceof Error ? err.message : 'Unknown error'}`,
          {
            url: `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/${alertId}`,
            method: 'PUT',
            category: 'alert',
            action: 'update_alert',
            alertId,
          },
          true
        );
        throw err; // Re-throw to let parent handle it
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const deleteAlert = useCallback(async (alertId: number, userId: number): Promise<boolean> => {
    setError(null);
    const alertName = getAlertName(alertId);
    try {
      const result = await dmgAlertApiService.deleteAlert(alertId, userId);
      if (result) {
        setAlerts(prev => prev.filter(alert => alert.id !== String(alertId)));
        setPagination(prev => ({
          ...prev,
          totalItems: Math.max(0, prev.totalItems - 1),
          totalPages: Math.max(0, Math.ceil(Math.max(0, prev.totalItems - 1) / prev.pageSize)),
        }));
      }
      await logAdminAction(
        `Alert ${alertName} deleted`,
        {
          url: `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/${alertId}`,
          method: 'DELETE',
          status: 200,
          category: 'alert',
          action: 'delete_alert',
          alertId,
          userId,
          details: [{ label: 'Alert ID', value: String(alertId) }],
        }
      );
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete alert');
      console.error('Error deleting alert:', err);
      await logAdminAction(
        `Failed to delete alert ${alertName}: ${err instanceof Error ? err.message : 'Unknown error'}`,
        { url: `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/${alertId}`, method: 'DELETE', category: 'alert', action: 'delete_alert', alertId },
        true
      );
      return false;
    }
  }, [getAlertName]);

  const activateAlert = useCallback(async (alertId: number, userId: number): Promise<boolean> => {
    setError(null);
    const alertName = getAlertName(alertId);
    try {
      await dmgAlertApiService.activateAlert(alertId, userId);
      // Update the alert status in local state
      setAlerts(prev =>
        prev.map(alert =>
          alert.id === String(alertId) ? { ...alert, status: 'Active' as const } : alert
        )
      );
      await logAdminAction(
        `Alert ${alertName} activated`,
        {
          url: `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/${alertId}/activate`,
          method: 'PATCH',
          status: 200,
          category: 'alert',
          action: 'activate_alert',
          alertId,
          userId,
          details: [{ label: 'Alert ID', value: String(alertId) }],
        }
      );
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate alert');
      console.error('Error activating alert:', err);
      await logAdminAction(
        `Failed to activate alert ${alertName}: ${err instanceof Error ? err.message : 'Unknown error'}`,
        { url: `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/${alertId}/activate`, method: 'PATCH', category: 'alert', action: 'activate_alert', alertId },
        true
      );
      return false;
    }
  }, [getAlertName]);

  const deactivateAlert = useCallback(async (alertId: number, userId: number): Promise<boolean> => {
    setError(null);
    const alertName = getAlertName(alertId);
    try {
      // Use deleteAlert API for deactivation (as per user's note)
      const result = await dmgAlertApiService.deleteAlert(alertId, userId);
      if (result) {
        // Update the alert status in local state instead of removing it
        setAlerts(prev =>
          prev.map(alert =>
            alert.id === String(alertId) ? { ...alert, status: 'Inactive' as const } : alert
          )
        );
      }
      await logAdminAction(
        `Alert ${alertName} deactivated`,
        {
          url: `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/${alertId}`,
          method: 'DELETE',
          status: 200,
          category: 'alert',
          action: 'deactivate_alert',
          alertId,
          userId,
          details: [{ label: 'Alert ID', value: String(alertId) }],
        }
      );
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate alert');
      console.error('Error deactivating alert:', err);
      await logAdminAction(
        `Failed to deactivate alert ${alertName}: ${err instanceof Error ? err.message : 'Unknown error'}`,
        { url: `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/${alertId}`, method: 'DELETE', category: 'alert', action: 'deactivate_alert', alertId },
        true
      );
      return false;
    }
  }, [getAlertName]);

  const sendTestAlert = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const result = await dmgAlertApiService.sendTestAlert(parseInt(id));
      if (result.success) {
        // You might want to show a success message here
        console.log('Test alert sent:', result.message);
        await logAdminAction(`Test alert sent for ${getAlertName(parseInt(id))}`, {
          url: `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/${id}/test`,
          method: 'POST',
          status: 200,
          category: 'alert',
          action: 'test_alert',
          alertId: parseInt(id),
        });
        return true;
      } else {
        setError(result.message);
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send test alert');
      console.error('Error sending test alert:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [getAlertName]);

  const subscribeToGlobalAlert = useCallback(async (id: string): Promise<boolean> => {
    setError(null);
    try {
      const result = await dmgAlertApiService.subscribeToGlobalAlert(parseInt(id));
      if (result.success) {
        console.log('Subscription successful:', result.message);
        await logAdminAction(`Subscribed to global alert ${getAlertName(parseInt(id))}`, {
          url: `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/${id}/subscribe`,
          method: 'POST',
          status: 200,
          category: 'alert',
          action: 'subscribe_alert',
          alertId: parseInt(id),
        });
        return true;
      } else {
        setError(result.message);
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to subscribe to alert');
      console.error('Error subscribing to alert:', err);
      return false;
    }
  }, [getAlertName]);

  const unsubscribeFromGlobalAlert = useCallback(async (id: string): Promise<boolean> => {
    setError(null);
    try {
      const result = await dmgAlertApiService.unsubscribeFromGlobalAlert(parseInt(id));
      if (result.success) {
        console.log('Unsubscription successful:', result.message);
        await logAdminAction(`Unsubscribed from global alert ${getAlertName(parseInt(id))}`, {
          url: `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/unsubscribe-thirty-days`,
          method: 'POST',
          status: 200,
          category: 'alert',
          action: 'unsubscribe_alert',
          alertId: parseInt(id),
        });
        return true;
      } else {
        setError(result.message);
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unsubscribe from alert');
      console.error('Error unsubscribing from alert:', err);
      return false;
    }
  }, [getAlertName]);

  // Subscribe to My Alert (by alertId)
  const subscribeToMyAlert = useCallback(
    async (userId: number, alertId: number): Promise<boolean> => {
      setError(null);
      try {
        const result = await dmgAlertApiService.subscribeToMyAlert(userId, alertId);
        if (result.success) {
          // Do not refetch; UI handles optimistic update
          await logAdminAction(`Subscribed to alert ${getAlertName(alertId)}`, {
            url: `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/subscribe`,
            method: 'POST',
            status: 200,
            category: 'alert',
            action: 'subscribe_alert',
            alertId,
            userId,
          });
          return true;
        } else {
          setError(result.message);
          return false;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to subscribe to alert');
        return false;
      }
    },
    [getAlertName]
  );

  const unsubscribeFromMyAlert = useCallback(
    async (userId: number, alertId: number): Promise<boolean> => {
      setError(null);
      try {
        const result = await dmgAlertApiService.unsubscribeFromMyAlert(userId, alertId);
        if (result.success) {
          // Do not refetch; UI handles optimistic update
          await logAdminAction(`Unsubscribed from alert ${getAlertName(alertId)}`, {
            url: `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/unsubscribe-thirty-days`,
            method: 'POST',
            status: 200,
            category: 'alert',
            action: 'unsubscribe_alert',
            alertId,
            userId,
          });
          return true;
        } else {
          setError(result.message);
          return false;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to unsubscribe from alert');
        return false;
      }
    },
    [getAlertName]
  );

  const refreshAlerts = useCallback(async () => {
    await fetchAlerts(pagination.currentPage, pagination.pageSize);
  }, [fetchAlerts]);

  // Initial load - don't auto-fetch, let the parent component control when to fetch
  // useEffect(() => {
  //   fetchAlerts();
  // }, [fetchAlerts]);

  return {
    alerts,
    loading,
    pagination,
    error,
    fetchAlerts,
    createAlert,
    updateAlert,
    deleteAlert,
    activateAlert,
    deactivateAlert,
    sendTestAlert,
    subscribeToGlobalAlert,
    unsubscribeFromGlobalAlert,
    subscribeToMyAlert,
    unsubscribeFromMyAlert,
    refreshAlerts,
  };
};
